/**
 * MentionService - Handles all mention-related operations
 */

import { getDateRangeForTimeframe, isSchoolRulePeriodError } from '../utils/dateHelpers';

import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { MentionRepository } from '../repositories/MentionRepository';
import {
  getOffenceField,
  generateClassId
} from '../utils/fieldNormalizer';
import {
  deriveSeverityFromLevel
} from '../utils/severityCalculator';
import {
  validateMentionData
} from '../utils/validationHelpers';
import { disciplineLogger } from '../../../utils/disciplineLogger';
import { mentionTypesService } from '../../mentionTypesService';
import { processingGuard } from '../utils/ProcessingGuard';
import { statsOrchestrator } from '../stats';
import { recordAwardForEvent } from '../../domainAwardResolver';
import { createMentionIssuanceId, deterministicMentionId } from '../utils/mentionIdentity';
import {
  assertRequiredMentionContext,
  getRequiredContextFields,
} from '../utils/mentionContextRequirements';

export class MentionService {
  constructor(mentionRepo = null) {
    this.mentionRepo = mentionRepo || new MentionRepository();
    this.offencesCollection = 'offences';
    this.categoriesCollection = 'disciplineCategories';
    this.defaultMentionTypeCache = null; // Cache default mention type
  }

  /**
   * Issue a new mention
   * @param {Object} mentionData - Mention data
   * @returns {Promise<Object>} Created mention
   */
  async issueMention(mentionData) {
    try {
      disciplineLogger.trace('Creating mention', mentionData, 'mentions');

      // Track this mention creation for loop detection
      const tempId = `${mentionData.studentId}-${Date.now()}`;
      processingGuard.checkMentionProcessing(tempId, 'create');

      // Validate required fields
      const validation = validateMentionData(mentionData);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }
      // Retain the request identity if a save/read response is lost.
      mentionData.issuanceId ||= createMentionIssuanceId();

      // Initialize offense details with safe defaults
      let offenseCategoryId = null;
      let offenseCategoryName = 'uncategorized';
      let offenceName = mentionData.offence || mentionData.reason || mentionData.offense;
      let offenseLevel = mentionData.offenseLevel || 1; // Use passed level if provided
      let offenseSeverity = 'low';
      let fullOffenseData = null;

      // Try to get full offense data
      if (mentionData.offenseId) {
        try {
          const offenseDoc = await getDoc(doc(
            db,
            'schools',
            mentionData.schoolId,
            this.offencesCollection,
            mentionData.offenseId,
          ));
          processingGuard.trackReads('MentionService.getOffense', 1);
          if (offenseDoc.exists()) {
            fullOffenseData = { id: offenseDoc.id, ...offenseDoc.data() };
          }
        } catch (error) {
          if (isSchoolRulePeriodError(error)) throw error;
          disciplineLogger.debug('Failed to fetch offense by ID', error, 'mentions');
        }
      }

      // Skip name-based lookup since getOffences() is not implemented
      // This was causing unnecessary operations without actual data retrieval
      // The offense name is already available from mentionData

      // Extract offense details if found
      let offenseMentionType = null;
      let offenseMentionTypeId = null;

      if (fullOffenseData) {
        disciplineLogger.debug('Found offense', fullOffenseData, 'mentions');
        offenceName = fullOffenseData.name || offenceToFind;
        // Only use offense.level if no specific level was passed
        if (!mentionData.offenseLevel) {
          offenseLevel = fullOffenseData.level || 1;
        }
        offenseSeverity = deriveSeverityFromLevel(offenseLevel);

        // Get category ID from offense data
        offenseCategoryId = fullOffenseData.categoryId || null;

        // Get mention type from offense configuration
        offenseMentionType = fullOffenseData.mentionType || null;
        offenseMentionTypeId = fullOffenseData.mentionTypeId || null;
      } else {
        offenseSeverity = deriveSeverityFromLevel(offenseLevel);
      }

      // Resolve category ID to name (optional - don't let this block mention creation)
      // This is a nice-to-have for categorization but not critical
      if (offenseCategoryId) {
        try {
          const categoryDoc = await getDoc(doc(db, this.categoriesCollection, offenseCategoryId));
          processingGuard.trackReads('MentionService.getCategory', 1);
          if (categoryDoc.exists()) {
            const category = categoryDoc.data();
            offenseCategoryName = category.name || 'uncategorized';
            disciplineLogger.debug(`Resolved category: ${offenseCategoryId} -> ${offenseCategoryName}`, null, 'mentions');
          } else {
            disciplineLogger.debug(`Category document not found: ${offenseCategoryId}`, null, 'mentions');
          }
        } catch (error) {
          if (isSchoolRulePeriodError(error)) throw error;
          // Non-critical error - use default category
          console.warn('Category resolution failed (non-blocking):', error);
        }
      } else {
        disciplineLogger.debug(`No category ID for offense: ${offenceName}`, null, 'mentions');
      }

      disciplineLogger.info('Offense resolution', {
        name: offenceName,
        level: offenseLevel,
        severity: offenseSeverity,
        categoryId: offenseCategoryId,
        categoryName: offenseCategoryName
      }, 'mentions');

      // Determine mention type - ID is source of truth, lookup value from ID
      let finalMentionTypeId = offenseMentionTypeId || mentionData.mentionTypeId;
      let finalMentionType = null;
      let finalMentionTypeConfig = null;

      // If we have an ID, lookup the mentionType document to get the canonical value
      // Use hierarchical path: schools/{schoolId}/mentionTypes/{id}
      if (finalMentionTypeId && mentionData.schoolId) {
        try {
          const mentionTypeRef = doc(db, 'schools', mentionData.schoolId, 'mentionTypes', finalMentionTypeId);
          const mentionTypeSnap = await getDoc(mentionTypeRef);
          if (mentionTypeSnap.exists()) {
            const mentionTypeData = mentionTypeSnap.data();
            finalMentionTypeConfig = { id: mentionTypeSnap.id, ...mentionTypeData };
            finalMentionType = mentionTypeData.value;
            disciplineLogger.info('📌 MENTION TYPE FROM ID LOOKUP', {
              mentionTypeId: finalMentionTypeId,
              resolvedValue: finalMentionType,
              label: mentionTypeData.label
            }, 'mentions');
          }
        } catch (error) {
          if (isSchoolRulePeriodError(error)) throw error;
          disciplineLogger.warn('Failed to lookup mentionType by ID', {
            mentionTypeId: finalMentionTypeId,
            error: error.message
          }, 'mentions');
        }
      }

      // Fallback to passed value if ID lookup failed or no ID
      if (!finalMentionType) {
        finalMentionType = offenseMentionType || mentionData.mentionType;
        disciplineLogger.info('📌 MENTION TYPE FROM PASSED VALUE (no ID or lookup failed)', {
          fromMentionData: mentionData.mentionType,
          fromOffense: offenseMentionType,
          resolved: finalMentionType
        }, 'mentions');
      }

      // Resolve configuration even when callers supplied only the type value.
      // Context requirements belong to school configuration, never to a label
      // or a hardcoded Mention type value in application logic.
      if (!finalMentionTypeConfig && finalMentionType && mentionData.schoolId) {
        finalMentionTypeConfig = await mentionTypesService.getByValue(
          finalMentionType,
          mentionData.schoolId,
        );
        if (finalMentionTypeConfig?.id && !finalMentionTypeId) {
          finalMentionTypeId = finalMentionTypeConfig.id;
        }
      }

      // If still no mention type, query for the school's default type
      if (!finalMentionType && !finalMentionTypeId) {
        try {
          const mentionTypes = await mentionTypesService.getAll(mentionData.schoolId);
          const defaultType = mentionTypes.find(t => t.isDefault);
          finalMentionType = defaultType?.value || mentionTypes[0]?.value || 'mention';
          finalMentionTypeId = defaultType?.id || mentionTypes[0]?.id || null;
          finalMentionTypeConfig = defaultType || mentionTypes[0] || null;

          disciplineLogger.info('⚠️ No mention type from offense - using configured default', {
            offenseName: offenceName,
            offenseId: mentionData.offenseId,
            fallbackType: finalMentionType,
            fallbackTypeId: finalMentionTypeId,
            isDefault: !!defaultType,
            message: 'Offense should be configured with mentionType field'
          }, 'mentions');
        } catch (error) {
          if (isSchoolRulePeriodError(error)) throw error;
          // If query fails, use hardcoded fallback
          finalMentionType = 'mention';
          finalMentionTypeId = null;
          disciplineLogger.warn('⚠️ Failed to query default mention type, using hardcoded fallback', {
            error: error.message
          }, 'mentions');
        }
      }

      assertRequiredMentionContext(
        [fullOffenseData, finalMentionTypeConfig],
        mentionData,
      );
      const subjectRequired = getRequiredContextFields(
        fullOffenseData,
        finalMentionTypeConfig,
      ).includes('subject');

      // Ensure offenseName is never undefined/null (fallback to "Unspecified Behavior")
      if (!offenceName) {
        disciplineLogger.warn('⚠️ OFFENSE NAME IS UNDEFINED', {
          studentName: mentionData.studentName,
          offenceFromData: mentionData.offence,
          reasonFromData: mentionData.reason,
          offenseFromData: mentionData.offense,
          offenseId: mentionData.offenseId,
          fullOffenseData: fullOffenseData,
          message: 'offenceName should NEVER be undefined - investigate why offense data is missing'
        }, 'mentions');
      }
      const finalOffenceName = offenceName || "Unspecified Behavior";

      // Build the mention data
      const mention = {
        studentId: mentionData.studentId,
        studentName: mentionData.studentName,
        offence: finalOffenceName,
        offense: finalOffenceName,
        reason: finalOffenceName,
        offenseId: mentionData.offenseId || null,
        className: mentionData.className || null,
        classId: mentionData.classId || generateClassId(mentionData.className),
        offenseCategory: offenseCategoryName,
        offenseCategoryId: offenseCategoryId,
        level: offenseLevel,
        severity: offenseSeverity,
        mentionType: finalMentionType,
        mentionTypeId: finalMentionTypeId,
        issuedById: mentionData.issuedById,
        issuedByName: mentionData.issuedByName,
        issuedByRole: mentionData.issuedByRole || 'teacher',
        notes: mentionData.notes || mentionData.details || '',
        subjectId: subjectRequired ? (mentionData.subjectId || null) : null,
        subject: subjectRequired ? (mentionData.subject || null) : null,
        subjectProvenance: subjectRequired
          ? (mentionData.subjectProvenance || null)
          : null,
        subjectContextRequired: subjectRequired,
        // Omitted incident time means current server capture, not historical entry.
        issuedAt: mentionData.issuedAt || null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        actioned: false,
        triggeredConsequences: [],
        schoolId: mentionData.schoolId || null,  // Multi-school support
        issuanceId: mentionData.issuanceId || null,
        incidentId: mentionData.incidentId || null,
        attachments: Array.isArray(mentionData.attachments)
          ? mentionData.attachments
          : [],
        ruleProcessingStatus: 'pending',
      };

      disciplineLogger.trace('Final mention data', mention, 'mentions');

      // Save the mention
      const deterministicId = deterministicMentionId({
        schoolId: mention.schoolId,
        issuedById: mention.issuedById,
        issuanceId: mention.issuanceId,
      });
      const savedMention = deterministicId
        ? await this.mentionRepo.createWithId(mention, deterministicId)
        : await this.mentionRepo.create(mention);
      disciplineLogger.critical(`Mention created: ${savedMention.id}`, { studentName: mention.studentName, offence: offenceName }, 'mentions');
      if (savedMention.wasCreated === false) return savedMention;

      // Sync className to student record if student's className is null but mention has one
      if (mention.className && mention.studentId && mention.schoolId) {
        try {
          const studentRef = doc(db, 'schools', mention.schoolId, 'students', mention.studentId);
          const studentSnap = await getDoc(studentRef);
          if (studentSnap.exists() && !studentSnap.data().className) {
            const { updateDoc } = await import('firebase/firestore');
            await updateDoc(studentRef, { className: mention.className });
            disciplineLogger.info('Synced className to student record', {
              studentId: mention.studentId,
              className: mention.className
            }, 'mentions');
          }
        } catch (err) {
          // Non-blocking - don't fail mention creation
          console.warn('Failed to sync className to student:', err.message);
        }
      }

      // NOTE: Cache invalidation moved to disciplineService AFTER rule processing completes
      // This prevents cache misses during rule evaluation (rules can reuse cached data)

      // Update aggregated stats (async, don't block)
      statsOrchestrator.onMentionCreated(savedMention).catch(err => {
        console.error('Failed to update stats:', err);
      });

      // Credit currency if the discipline domain (or its mention type) declares one.
      // Fire-and-forget — must never block mention creation.
      if (savedMention.schoolId) {
        recordAwardForEvent({
          schoolId: savedMention.schoolId,
          domainId: 'discipline',
          typesCollection: 'mentionTypes',
          typeId: finalMentionTypeId,
          eventId: savedMention.id,
          event: {
            studentId: savedMention.studentId,
            studentName: savedMention.studentName,
            className: savedMention.className,
            issuedById: savedMention.issuedById,
            issuedByName: savedMention.issuedByName,
            typeLabel: savedMention.mentionType,
            issuedAt: savedMention.issuedAt,
            createdAt: savedMention.createdAt,
          },
        }).catch(err => console.error('[MentionService] currency award failed:', err.message));
      }

      return savedMention;
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error creating mention:', error);
      throw error;
    }
  }

  /**
   * Get today's mentions
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} Today's mentions
   */
  async getTodaysMentions(filters = {}) {
    try {
      const options = {
        ...filters
      };

      return await this.mentionRepo.getTodayMentions(options);
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error fetching today\'s mentions:', error);
      return [];
    }
  }

  /**
   * Subscribe to today's mentions
   * @param {Function} callback - Callback for updates
   * @param {Object} filters - Optional filters (schoolId, studentId, classId)
   * @returns {Function} Unsubscribe function
   */
  subscribeTodaysMentions(callback, filters = {}) {
    // CRITICAL: Never query without schoolId - would violate security rules
    if (!filters.schoolId) {
      console.error('[MentionService] Attempted to subscribe without schoolId - returning empty mentions');
      callback([]);
      return () => {}; // Return no-op unsubscribe
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const constraints = [
      { field: 'issuedAt', operator: '>=', value: Timestamp.fromDate(today) }
    ];

    // Multi-school support: filter by schoolId (REQUIRED)
    constraints.push({ field: 'schoolId', operator: '==', value: filters.schoolId });

    if (filters.studentId) {
      constraints.push({ field: 'studentId', operator: '==', value: filters.studentId });
    }

    if (filters.classId) {
      constraints.push({ field: 'classId', operator: '==', value: filters.classId });
    }

    try {
      return this.mentionRepo.subscribe(callback, constraints);
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('[MentionService] Error setting up subscription:', error);
      callback([]);
      return () => {}; // Return no-op unsubscribe
    }
  }

  /**
   * Update a mention
   * @param {string} mentionId - Mention ID
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} Updated mention
   */
  async updateMention(mentionId, updates) {
    try {
      return await this.mentionRepo.update(mentionId, updates);
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error updating mention:', error);
      throw error;
    }
  }

  /**
   * Delete a mention
   * @param {string} mentionId - Mention ID
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<void>}
   */
  async deleteMention(mentionId, schoolId) {
    try {
      return await this.mentionRepo.delete(mentionId, schoolId);
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error deleting mention:', error);
      throw error;
    }
  }

  /**
   * Get mentions for a specific student
   * @param {string} studentId - Student ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Student mentions
   */
  async getMentionsByStudent(studentId, options = {}) {
    try {
      return await this.mentionRepo.getByStudentId(studentId, options);
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error fetching student mentions:', error);
      return [];
    }
  }

  /**
   * Get mentions for a specific class
   * @param {string} classId - Class ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Class mentions
   */
  async getMentionsByClass(classId, options = {}) {
    try {
      return await this.mentionRepo.getByClassId(classId, options);
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error fetching class mentions:', error);
      return [];
    }
  }

  /**
   * Get mentions for rule evaluation with scope
   * @param {Object} mention - Current mention
   * @param {string} scope - Scope type
   * @param {number} timeCount - Time count
   * @param {string} timeframe - Timeframe
   * @returns {Promise<Array>} Mentions matching scope
   */
  async getMentionsForScope(mention, scope, timeCount, timeframe) {
    try {
      const dateRange = await getDateRangeForTimeframe(timeframe, timeCount, mention.schoolId, mention.issuedAt || mention.createdAt);
      const mentions = await this.mentionRepo.getMentionsForRuleEvaluation(
        mention.studentId,
        { schoolId: mention.schoolId },
        dateRange.start,
        dateRange.end
      );

      // Apply scope-specific filtering
      return mentions.filter(m => {
        switch (scope) {
          case 'same-offense':
            return m.reason === mention.reason;

          case 'same-category': {
            const currentCategory = mention.offenseCategory || 'uncategorized';
            const mentionCategory = m.offenseCategory || 'uncategorized';
            return currentCategory === mentionCategory;
          }

          case 'same-lesson': {
            if (m.classId !== mention.classId) return false;
            const currentTime = mention.issuedAt?.toDate?.() || new Date(mention.issuedAt);
            const mentionTime = m.issuedAt?.toDate?.() || new Date(m.issuedAt);
            const timeDiff = Math.abs(currentTime - mentionTime);
            return timeDiff <= 2 * 60 * 60 * 1000; // 2 hours
          }

          case 'any':
            return true;

          default:
            disciplineLogger.debug(`Unknown scope: ${scope}`, null, 'mentions');
            return false;
        }
      });
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error getting mentions for scope:', error);
      return [];
    }
  }

  /**
   * Get mention count for a timeframe
   * @param {string} studentId - Student ID
   * @param {string} timeframe - Timeframe
   * @param {Object} options - Additional options
   * @returns {Promise<number>} Mention count
   */
  async getMentionCountForTimeframe(studentId, timeframe, options = {}) {
    try {
      const dateRange = await getDateRangeForTimeframe(timeframe, 1, options.schoolId, options.referenceDate);
      const mentions = await this.mentionRepo.getByStudentId(studentId, {
        startDate: dateRange.start,
        endDate: dateRange.end,
        actioned: options.actioned,
        schoolId: options.schoolId
      });

      return mentions.length;
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error counting mentions:', error);
      return 0;
    }
  }

  /**
   * Get previous mentions by type
   * @param {string} studentId - Student ID
   * @param {string} offenseType - Offense type
   * @param {number} days - Days to look back
   * @returns {Promise<Array>} Previous mentions
   */
  async getPreviousMentionsByType(studentId, offenseType, days = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const mentions = await this.mentionRepo.getByStudentId(studentId, {
        startDate,
        actioned: false
      });

      return mentions.filter(m =>
        getOffenceField(m).toLowerCase().includes(offenseType.toLowerCase())
      );
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error fetching previous mentions by type:', error);
      return [];
    }
  }

  /**
   * Mark mentions as actioned
   * @param {Array} mentionIds - Mention IDs
   * @param {Object} actionDetails - Action details
   * @returns {Promise<Array>} Updated mentions
   */
  async markMentionsAsActioned(mentionIds, actionDetails = {}) {
    try {
      return await this.mentionRepo.batchMarkAsActioned(mentionIds, actionDetails);
    } catch (error) {
      if (isSchoolRulePeriodError(error)) throw error;
      console.error('Error marking mentions as actioned:', error);
      throw error;
    }
  }

  /**
   * Helper: Get date range for timeframe
   * @private
   */
  getDateRangeForTimeframe(timeframe, timeCount = 1, schoolId = null, referenceDate = new Date()) {
    return getDateRangeForTimeframe(timeframe, timeCount, schoolId, referenceDate);
  }

}
